import { BusinessUser } from '@models/businesses';
import { Encumbrance } from '@models/encumbrance';
import { ComplianceRequest } from '@models/jurisdictions';
import {
	ActingForType,
	BusinessMatterClientWithAssociations,
	Capacity,
	ClientsCapacity,
	MatterBusinessUser,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import {
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import {
	MatterStewartTitleInsurance,
	Region,
	Site,
	StewartStatus,
	SubmitFile,
} from '@models/title-insurance/stewart';
import { TitleInsuranceProvider } from '@models/title-insurance';
import { UserProfile } from '@models/userProfile';
import { Selector } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { UserProfileState } from '@state/user-profile';
import { isEqual, cloneDeep } from 'lodash';
import { MatterClients } from '../matter_clients';
import {
	addDeletedGUIDs,
	buildMortgages,
	buildParties,
	buildProperty,
	mapGUIDs,
	removeUndefinedAndNull,
} from './stewart.util';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { isSet } from '@core/util/value';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';

export class StewartSelectors {
	@Selector([
		UserProfileState.getCurrentBusinessId,
		SelectedMatter.id,
		SelectedMatter.matterNumber,
		SelectedMatter.matterFileType,
		SelectedMatter.closingDate,
		SelectedMatter.getTitleInsuranceResolvedPurchasePrice,
		SelectedMatter.otherPartyLawFirm,
		SelectedMatter.propertyDetails,
		MatterClients.get_mortgagorsWithAssociationMapped,
		MatterClients.get_otherPartyWithAssociationMapped,
		MatterMortgagesState.getAllMortgagesWithAssociations,
		MatterEncumbrancesState.getRemain,
		SelectedMatter.titleInsurancePreviousSubmittedPayload,
		SelectedMatter.capacity,
		SelectedMatter.titleInsuranceThirdPartyId,
		MatterJurisdictionSelectors.getAllComplianceRequests,
		MatterClients.get_clientsCapacity,
		SelectedMatter.actingFor,
		BusinessUsersState.getUsers,
		SelectedMatter.assignedLawyer,
		UserProfileState.getProfile,
		SelectedMatter.getTitleInsuranceProvider,
		MatterTitlesState.getAll,
	])
	static getStewartDataPayload(
		businessId: string,
		matterId: string,
		matterNumber: string,
		fileType: MatterFileType,
		closingDate: string,
		purchasePrice: number,
		otherPartyFirm: LawFirm,
		propertyDetails: MatterPropertyDetails,
		purchasers: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		vendors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		allMortgages: MatterMortgageWithAssociations[],
		remain: Encumbrance[],
		previousPayload: SubmitFile,
		capacity: Capacity,
		thirdPartyId: number,
		complianceRequests: ComplianceRequest[],
		clientsCapacity: ClientsCapacity,
		actingFor: ActingForType,
		allUsers: BusinessUser[],
		assignedLawyer: MatterBusinessUser,
		user: UserProfile,
		provider: TitleInsuranceProvider,
		matterTitles: MatterTitle[],
	): SubmitFile {
		// map file data

		if (!matterId || provider !== TitleInsuranceProvider.STEWART) {
			return null;
		}

		const mortgages = allMortgages?.filter(
			(x) => x.transactionType !== TransactionType.BRIDGE && !x.mms,
		);

		const ProfileID = allUsers?.find(
			(x) => x.id === assignedLawyer?.id,
		)?.stewartProfileId;

		const file: SubmitFile = {
			UniqueId: null,
			CloserId: matterId,
			LawyerFileNo: matterNumber,
			ClosingDate: closingDate,
			RegionID: Region.Ontario,
			CSISID: businessId, //'29da24be080f4e118c0b819644954ba5',
			Properties: [],
			Mortgages: [],
			Parties: [],
			TreefortTransactionKeys: [],
			ProcessedBy: `${user.firstName} ${user.lastName}`,
			SiteTypeID: Site.Single,
			ProfileID,
		};

		if (thirdPartyId && thirdPartyId > 0) {
			file.FileNo = thirdPartyId;
		}

		const property = buildProperty(
			matterId,
			purchasePrice,
			otherPartyFirm,
			purchasers,
			vendors,
			propertyDetails,
			capacity?.capacityType,
			complianceRequests,
			fileType,
			actingFor,
			matterTitles,
		);

		file.Properties.push(property);
		file.Mortgages = buildMortgages(mortgages, remain, fileType, closingDate);
		file.Parties = buildParties(purchasers, vendors, clientsCapacity);
		file.TreefortTransactionKeys = buildTreefortTransactionKeys(purchasers);

		// map guids / insert missing guids
		const mapped = mapGUIDs(previousPayload, file);

		let result = mapped;
		if (previousPayload) {
			result = addDeletedGUIDs(previousPayload, mapped);
		}

		return result;
	}

	@Selector([SelectedMatter.getTitleInsurance])
	static previousPayload(
		titleInsurance: MatterStewartTitleInsurance,
	): SubmitFile {
		return titleInsurance?.previousSubmittedPayload;
	}

	@Selector([
		StewartSelectors.getStewartDataPayload,
		SelectedMatter.getTitleInsurance,
	])
	static getPayloadHasChanged(
		curr: SubmitFile,
		titleInsurance: MatterStewartTitleInsurance,
	): boolean {
		let payloadHasChanged = true;

		if (titleInsurance?.provider !== TitleInsuranceProvider.STEWART) {
			return false;
		}

		if (!titleInsurance?.previousSubmittedPayload) {
			return false;
		}

		if (titleInsurance?.previousSubmittedPayload && !curr) {
			return true;
		}

		const previousPayload = cloneDeep(titleInsurance?.previousSubmittedPayload);
		const currentPayload = cloneDeep(curr);

		if (
			isEqual(
				removeUndefinedAndNull(previousPayload),
				removeUndefinedAndNull(currentPayload),
			)
		) {
			payloadHasChanged = false;
		}

		return payloadHasChanged;
	}

	@Selector([
		StewartSelectors.getPayloadHasChanged,
		SelectedMatter.getTitleInsurance,
	])
	static payloadWarning(
		payloadChanged: boolean,
		titleInsurance: MatterStewartTitleInsurance,
	): boolean {
		if (
			titleInsurance?.provider !== TitleInsuranceProvider.STEWART ||
			[StewartStatus.CANCELLED, StewartStatus.DECLINED].includes(
				titleInsurance?.state,
			)
		) {
			return false;
		}

		return payloadChanged;
	}
}
function buildTreefortTransactionKeys(
	purchasers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
): string[] {
	return (
		purchasers?.reduce((prev, curr) => {
			const transactionKey = (curr as PersonMatterClient)?.idv?.transactionKey;
			if (isSet(transactionKey) && !prev.includes(transactionKey)) {
				prev.push(transactionKey);
			}
			return prev;
		}, [] as string[]) || []
	);
}
